if (typeof ScrollingText !== "function") {
  class ScrollingText extends HTMLElement {

    constructor() {
      super();
    }
    
    connectedCallback() {
      requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      if (this._logoMotion) {
        this._logoAnimations?.forEach(animation => animation.cancel());
        this._intersectionObserver?.disconnect();
        this._resizeObserver?.disconnect();
        this.removeEventListener("mouseenter", this._onLogoEnter);
        this.removeEventListener("mouseleave", this._onLogoLeave);
        document.removeEventListener("visibilitychange", this._onLogoVisibility);
        return;
      }
      this.stop();
      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      window.removeEventListener("blur", this._onBlur);
      window.removeEventListener("focus", this._onFocus);

      this.removeEventListener("mouseover", this._onMouseOver);
      this.removeEventListener("mouseout", this._onMouseOut);
    }

    init() {
      if (!this.isConnected) return;
      if (this.classList.contains("scrolling-images-parent--logos")) {
        this._initLogoMotion();
        return;
      }

      this._running = false;
      this._position = 0;

      this._speed = window.innerWidth > 768
        ? parseFloat(this.dataset.scrollingSpeed)
        : parseFloat(this.dataset.scrollingSpeed) / 1.5;

      this._direction = this.dataset.scrollingDirection || "ltr";
      this._pauseOnHover = this.dataset.pauseOnHover === "true";
      this._hoverEaseDuration = this.dataset.pauseOnHoverEase === "true" ? 1 : 0;

      this._lastTime = null;
      this._velocityMultiplier = 1;
      this._targetMultiplier = 1;

      this._loop = this._loop.bind(this);
      this._onMouseOver = this._onMouseOver.bind(this);
      this._onMouseOut = this._onMouseOut.bind(this);
      this._onResize = this._onResize.bind(this);

      this._base = this.children[0];
      if (!this._base) return;

      this._base.style.position = "relative";

      for (const child of this.children) {
        child.style.willChange = "transform";
        child.style.transform = "translate3d(0,0,0)";
      }

      this._template = this._base.cloneNode(true);

      this._readSizes();
      this._setupChildren();

      this._setupHoverPause();
      this._setupIntersectionObserver();
      this._setupResizeObserver();

      this.start();
    }

    _initLogoMotion() {
      this._logoMotion = true;
      this._base = this.firstElementChild;
      if (!this._base) return;
      while (this.children.length > 1) this.lastElementChild.remove();
      this._logoTemplate = this._base.cloneNode(true);
      this._logoAnimations = [];
      this._logoWidth = 0;
      this._logoDuration = 0;
      this._logoVisible = false;
      this._logoHover = this.dataset.pauseOnHover === "true" && this.matches(":hover");
      this._onLogoEnter = () => {
        this._logoHover = true;
        this._updateLogoPlayback();
      };
      this._onLogoLeave = () => {
        this._logoHover = false;
        this._updateLogoPlayback();
      };
      this._onLogoVisibility = () => this._updateLogoPlayback();
      if (this.dataset.pauseOnHover === "true") {
        this.addEventListener("mouseenter", this._onLogoEnter);
        this.addEventListener("mouseleave", this._onLogoLeave);
      }
      document.addEventListener("visibilitychange", this._onLogoVisibility);
      this._resizeObserver = new ResizeObserver(() => this._resizeLogoMotion());
      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this._base);
      this._intersectionObserver = new IntersectionObserver(entries => {
        this._logoVisible = entries[0].isIntersecting;
        this._updateLogoPlayback();
      });
      this._intersectionObserver.observe(this);
      this._resizeLogoMotion();
    }

    _resizeLogoMotion() {
      const width = this._base.getBoundingClientRect().width;
      const viewportWidth = this.getBoundingClientRect().width;
      if (width <= 0 || viewportWidth <= 0) return;
      const count = Math.max(2, Math.ceil(viewportWidth / width) + 1);
      if (width === this._logoWidth && count === this.children.length) return;
      const previousTime = this._logoAnimations[0]?.currentTime ?? 0;
      const progress = this._logoDuration > 0 ? (previousTime / this._logoDuration) % 1 : 0;
      this._logoAnimations.forEach(animation => animation.cancel());
      while (this.children.length > count) this.lastElementChild.remove();
      while (this.children.length < count) {
        const clone = this._logoTemplate.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
        clone.querySelectorAll("[id]").forEach(element => element.removeAttribute("id"));
        clone.querySelectorAll("[data-shopify-editor-block]").forEach(element => element.removeAttribute("data-shopify-editor-block"));
        this.appendChild(clone);
      }
      const speed = Math.max(1, parseFloat(this.dataset.scrollingSpeed) || 51);
      const distance = this.dataset.scrollingDirection === "rtl" ? width : -width;
      const duration = width / speed * 1000;
      this._logoWidth = width;
      this._logoDuration = duration;
      this._logoAnimations = Array.from(this.children, child => {
        child.style.willChange = "transform";
        child.style.transition = "none";
        const animation = child.animate([
          { transform: "translate3d(0, 0, 0)" },
          { transform: `translate3d(${distance}px, 0, 0)` }
        ], { duration, iterations: Infinity, easing: "linear" });
        animation.pause();
        animation.currentTime = progress * duration;
        return animation;
      });
      this._updateLogoPlayback();
    }

    _updateLogoPlayback() {
      const shouldPlay = this._logoVisible && !this._logoHover && !document.hidden;
      this._logoAnimations.forEach(animation => {
        if (shouldPlay && animation.playState !== "running") animation.play();
        if (!shouldPlay && animation.playState !== "paused") animation.pause();
      });
    }

    _readSizes() {
      const rectBox = this.getBoundingClientRect();
      const rectChild = this._base.getBoundingClientRect();
      this._boxWidth = rectBox.width;
      this._itemWidth = rectChild.width;
    }

    _neededChildren() {
      return Math.ceil(this._boxWidth / this._itemWidth) + 1;
    }

    _setupChildren() {
      const needed = this._neededChildren();
      const current = this.children.length;

      if (needed > current) {
        for (let i = current; i < needed; i++) {
          const clone = this._template.cloneNode(true);
          clone.style.willChange = "transform";
          clone.style.transform = "translate3d(0,0,0)";
          this.appendChild(clone);
        }
      } else if (needed < current) {
        for (let i = current; i > needed; i--) {
          this.removeChild(this.lastElementChild);
        }
      }
    }

    start() {
      if (this._running) return;
      this._running = true;
      this._lastTime = null;
      requestAnimationFrame(this._loop);
    }

    stop() {
      this._running = false;
    }

    _loop(timestamp) {
      if (!this._running) return;

      if (!this._lastTime) this._lastTime = timestamp;
      const delta = timestamp - this._lastTime;
      this._lastTime = timestamp;

      if (this._velocityMultiplier !== this._targetMultiplier) {
        const easeDelta = delta / (this._hoverEaseDuration * 1000);
        if (this._targetMultiplier < this._velocityMultiplier) {
          this._velocityMultiplier = Math.max(this._targetMultiplier, this._velocityMultiplier - easeDelta);
        } else {
          this._velocityMultiplier = Math.min(this._targetMultiplier, this._velocityMultiplier + easeDelta);
        }
      }

      const p = this._position;
      const dir = this._direction;

      const transformValue = dir === "rtl"
        ? `translate3d(${p}px, 0, 0)`
        : `translate3d(${-p}px, 0, 0)`;

      for (const el of this.children) {
        el.style.transform = transformValue;
      }

      this._position += (this._speed * this._velocityMultiplier * delta) / 1000;
      if (this._position >= this._itemWidth) {
        this._position = this._position % this._itemWidth;
      }

      requestAnimationFrame(this._loop);
    }

    _setupHoverPause() {
      if (!this._pauseOnHover) return;

      this._windowInFocus = true;

      this._onBlur = () => (this._windowInFocus = false);
      this._onFocus = () => (this._windowInFocus = true);

      window.addEventListener("blur", this._onBlur);
      window.addEventListener("focus", this._onFocus);

      this.addEventListener("mouseover", this._onMouseOver);
      this.addEventListener("mouseout", this._onMouseOut);
    }

    _onMouseOver() {
      if (!this._windowInFocus) return;
      if (this._hoverEaseDuration > 0) {
        this._targetMultiplier = 0;
      } else {
        this.stop();
      }
    }

    _onMouseOut() {
      if (!this._windowInFocus) return;
      if (this._hoverEaseDuration > 0) {
        this._targetMultiplier = 1;
      } else {
        this.start();
      }
    }

    _setupIntersectionObserver() {
      this._intersectionObserver = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) this.start();
        else this.stop();
      });
      this._intersectionObserver.observe(this);
    }

    _setupResizeObserver() {
      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this);
    }

    _onResize() {
      if (!this._running) return;
      this._readSizes();
      this._setupChildren();
    }

  }

  if ( typeof customElements.get("scrolling-text") == 'undefined' ) {
    customElements.define("scrolling-text", ScrollingText);
  }


}
